import {
  DataStatusSchema,
  type FeedbackRequest,
  QueryResponseSchema,
  type QueryRequest,
  type QueryResponse,
} from "../../../../shared/contracts/query";
import {
  BillingErrorResponseSchema,
  QuotaResponseSchema,
  RedeemResponseSchema,
  type QuotaResponse,
  type RedeemResponse,
} from "../../../../shared/contracts/billing";
export type QueryClientErrorCode =
  | "rate_limited"
  | "validation"
  | "quota_exhausted"
  | "http"
  | "invalid_response"
  | "aborted"
  | "timeout"
  | "network";

const MESSAGE_BY_CODE: Record<QueryClientErrorCode, string> = {
  rate_limited: "查询过于频繁，请稍后重试",
  validation: "查询条件无效，请检查品名或编码",
  quota_exhausted: "今日免费次数已用完，请兑换查询次数",
  http: "查询暂时不可用，请稍后重试",
  invalid_response: "查询结果暂时不可用，请稍后重试",
  aborted: "查询已取消",
  timeout: "查询超时，请稍后重试",
  network: "网络暂时不可用，请稍后重试",
};

/** A stable, intentionally small error surface for the public UI. */
export class QueryClientError extends Error {
  public readonly code: QueryClientErrorCode;
  public readonly retryAfterSeconds: number | null;

  public constructor(code: QueryClientErrorCode, options: { retryAfterSeconds?: number | null } = {}) {
    super(MESSAGE_BY_CODE[code]);
    this.name = "QueryClientError";
    this.code = code;
    this.retryAfterSeconds = options.retryAfterSeconds ?? null;
  }
}

function clientError(code: QueryClientErrorCode): QueryClientError {
  return new QueryClientError(code);
}

export type QueryRequestWithSession = QueryRequest & { querySessionId?: string };

export type BillingClientErrorCode =
  | "restore_confirmation_required"
  | "invalid_redemption"
  | "quota_exhausted"
  | "http"
  | "invalid_response"
  | "aborted"
  | "network";

const BILLING_MESSAGE_BY_CODE: Record<BillingClientErrorCode, string> = {
  restore_confirmation_required: "请确认切换到兑换码原钱包",
  invalid_redemption: "兑换码无效或已失效",
  quota_exhausted: "今日免费次数已用完，请兑换查询次数",
  http: "操作暂时不可用，请稍后重试",
  invalid_response: "服务响应暂时不可用，请稍后重试",
  aborted: "操作已取消",
  network: "网络暂时不可用，请稍后重试",
};

export class BillingClientError extends Error {
  public readonly code: BillingClientErrorCode;
  public readonly status: number | null;

  public constructor(code: BillingClientErrorCode, status: number | null = null) {
    super(BILLING_MESSAGE_BY_CODE[code]);
    this.name = "BillingClientError";
    this.code = code;
    this.status = status;
  }
}

function abortKind(error: unknown, signal?: AbortSignal): QueryClientErrorCode | null {
  if (error instanceof DOMException && error.name === "TimeoutError") return "timeout";
  if (error instanceof DOMException && error.name === "AbortError") return "aborted";
  if (typeof error === "object" && error !== null && "name" in error) {
    const name = (error as { name?: unknown }).name;
    if (name === "TimeoutError") return "timeout";
    if (name === "AbortError") return "aborted";
  }
  if (signal?.aborted) return "aborted";
  return null;
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    throw clientError("invalid_response");
  }
}

async function readBillingJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    throw new BillingClientError("invalid_response", response.status);
  }
}

const KNOWN_BILLING_ERROR_CODES = new Set<BillingClientErrorCode>([
  "restore_confirmation_required",
  "invalid_redemption",
  "quota_exhausted",
]);

async function billingError(response: Response): Promise<never> {
  let payload: unknown;
  try {
    payload = await response.clone().json();
  } catch {
    payload = undefined;
  }
  const parsed = BillingErrorResponseSchema.safeParse(payload);
  const code = parsed.success && KNOWN_BILLING_ERROR_CODES.has(parsed.data.code as BillingClientErrorCode)
    ? parsed.data.code as BillingClientErrorCode
    : "http";
  throw new BillingClientError(code, response.status);
}

function billingAbortKind(error: unknown, signal?: AbortSignal): "aborted" | null {
  if (error instanceof DOMException && error.name === "AbortError") return "aborted";
  if (typeof error === "object" && error !== null && "name" in error && (error as { name?: unknown }).name === "AbortError") return "aborted";
  if (signal?.aborted) return "aborted";
  return null;
}

async function requestBilling<T>(
  path: string,
  init: RequestInit,
  schema: { safeParse: (value: unknown) => { success: boolean; data?: T } },
): Promise<T> {
  let response: Response;
  const signal = init.signal instanceof AbortSignal ? init.signal : undefined;
  try {
    response = await fetch(path, init);
  } catch (error) {
    const kind = billingAbortKind(error, signal);
    if (kind) throw new BillingClientError(kind);
    throw new BillingClientError("network");
  }
  if (!response.ok) return billingError(response);
  const parsed = schema.safeParse(await readBillingJson(response));
  if (!parsed.success) throw new BillingClientError("invalid_response", response.status);
  return parsed.data as T;
}

export async function getDataStatus(signal?: AbortSignal, ruleDate?: string): Promise<QueryResponse["dataStatus"]> {
  let response: Response;
  try {
    const path = ruleDate ? `/api/status?ruleDate=${encodeURIComponent(ruleDate)}` : "/api/status";
    response = await fetch(path, signal ? { signal } : {});
  } catch (error) {
    const kind = abortKind(error, signal);
    if (kind) throw clientError(kind);
    throw clientError("network");
  }

  if (!response.ok) throw clientError("http");
  const parsed = DataStatusSchema.safeParse(await readJson(response));
  if (!parsed.success) throw clientError("invalid_response");
  return parsed.data;
}

export async function queryCustoms(request: QueryRequestWithSession, signal?: AbortSignal): Promise<QueryResponse> {
  let response: Response;
  try {
    response = await fetch("/api/query", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(request),
      ...(signal ? { signal } : {}),
    });
  } catch (error) {
    const kind = abortKind(error, signal);
    if (kind) throw clientError(kind);
    throw clientError("network");
  }

  if (!response.ok) {
    if (response.status === 429) {
      const retryAfter = Number(response.headers.get("retry-after"));
      throw new QueryClientError("rate_limited", { retryAfterSeconds: Number.isFinite(retryAfter) ? retryAfter : null });
    }
    if (response.status === 402) {
      let payload: unknown;
      try { payload = await response.clone().json(); } catch { payload = undefined; }
      const parsed = BillingErrorResponseSchema.safeParse(payload);
      if (parsed.success && parsed.data.code === "quota_exhausted") throw clientError("quota_exhausted");
      throw clientError("http");
    }
    if (response.status === 400) throw clientError("validation");
    throw clientError("http");
  }

  const payload = await readJson(response);
  const parsed = QueryResponseSchema.safeParse(payload);
  if (!parsed.success) throw clientError("invalid_response");
  return parsed.data;
}

export async function submitFeedback(request: FeedbackRequest): Promise<void> {
  let response: Response;
  try {
    response = await fetch("/api/feedback", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(request),
    });
  } catch (error) {
    const kind = abortKind(error);
    if (kind) throw clientError(kind);
    throw clientError("network");
  }
  if (!response.ok) throw clientError("http");
}

export async function getQuota(signal?: AbortSignal): Promise<QuotaResponse> {
  return requestBilling("/api/quota", signal ? { signal } : {}, QuotaResponseSchema);
}

export async function redeemCode(code: string, confirmRestore = false): Promise<RedeemResponse> {
  const normalizedCode = code.trim();
  const body = confirmRestore ? { code: normalizedCode, confirmRestore: true } : { code: normalizedCode };
  return requestBilling(
    "/api/redeem",
    { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) },
    RedeemResponseSchema,
  );
}
